import React, { useState, useEffect } from 'react';
import CityCard from './CityCard';
import AlertsPanel from './AlertsPanel';
import LoadingSpinner from './LoadingSpinner';
import { oceanDataService } from '../services/oceanDataService';

const Dashboard = () => {
  const [cities, setCities] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [sortBy, setSortBy] = useState('temperature');

  useEffect(() => {
    loadData();
    // Set up auto-refresh every 10 minutes
    const interval = setInterval(loadData, 600000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [citiesData, alertsData] = await Promise.all([
        oceanDataService.getCitiesData(),
        oceanDataService.getActiveAlerts()
      ]);
      
      setCities(citiesData);
      setAlerts(alertsData);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const sortedCities = [...cities].sort((a, b) => {
    switch (sortBy) {
      case 'temperature':
        return (b.temperature || 0) - (a.temperature || 0);
      case 'alert':
        return b.alertLevel.localeCompare(a.alertLevel);
      case 'name':
        return a.name.localeCompare(b.name);
      default:
        return 0;
    }
  });

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="text-2xl font-bold text-blue-600">{cities.length}</div>
          <div className="text-sm text-gray-600">Monitored Cities</div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="text-2xl font-bold text-red-600">
            {alerts.filter(a => a.level === 'alert').length}
          </div>
          <div className="text-sm text-gray-600">Critical Alerts</div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="text-2xl font-bold text-yellow-600">
            {alerts.filter(a => a.level === 'watch').length}
          </div>
          <div className="text-sm text-gray-600">Watch Alerts</div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="text-2xl font-bold text-green-600">
            {cities.filter(c => c.alertLevel === 'normal').length}
          </div>
          <div className="text-sm text-gray-600">Normal Status</div>
        </div>
      </div>

      {/* Active Alerts */}
      {alerts.length > 0 && <AlertsPanel alerts={alerts} />}

      {/* Controls */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">Ocean Temperature by City</h2>
            {lastUpdate && (
              <p className="text-sm text-gray-600">
                Last updated: {lastUpdate.toLocaleString('en-PH')}
              </p>
            )}
          </div>
          <div className="flex gap-4">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="temperature">Sort by Temperature</option>
              <option value="alert">Sort by Alert Level</option>
              <option value="name">Sort by Name</option>
            </select>
            <button
              onClick={loadData}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Refresh Data
            </button>
          </div>
        </div>
      </div>

      {/* Cities Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sortedCities.map((city) => (
          <CityCard key={city.id} city={city} />
        ))}
      </div>
    </div>
  );
};

export default Dashboard;